import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Table } from 'antd';
import dayjs from 'dayjs';

import React, { useContext, useEffect, useRef, useState } from 'react';
import systemConfig from '../../../../config/systematic';
import fetchApi from '../../../../utils/api/fetchApi';
import getList from '../../../universal/getList';
import antMessage from '../../../../utils/extend/AntdNotification';
import 'dayjs/locale/zh-cn';
import { showCustomNotification, useNotificationInstance } from '../../../../utils/extend/AntdNotificationEx';
import { SocketContext } from '../../../../App';
import moment from 'moment';
import entrepotPrint from '../../../../generalMethod/entrepotPrint';
import DownloadExcel from '../../../report/DownloadExcel';

const { RangePicker } = DatePicker;

/**
 * 合计规则：
 * 期初、本期进货、本期结存
 * 先合计数量，再合计金额
 */
const calculateSummary = (pageData: readonly any[]) => {
    const summary = {
        openingQuantity: 0,
        openingPrice: 0,

        importQuantity: 0,
        importPrice: 0,

        balanceQuantity: 0,
        balancePrice: 0,

        incomeStatementQuantity: 0,
        incomeStatementPrice: 0,

        exportQuantity: 0,
        exportPrice: 0,
    };

    pageData.forEach((item) => {

        if (!item || typeof item !== 'object') return;

        // 期初：先数量，后金额
        summary.openingQuantity += Number(item.openingQuantity) || 0;
        summary.openingPrice += Number(item.openingPrice) || 0;

        // 本期进货：先数量，后金额
        summary.importQuantity += Number(item.importQuantity) || 0;
        summary.importPrice += Number(item.importPrice) || 0;

        // 本期结存：先数量，后金额
        summary.balanceQuantity += Number(item.balanceQuantity) || 0;
        summary.balancePrice += Number(item.balancePrice) || 0;

        // 本期调整保留原结构
        summary.incomeStatementQuantity += Number(item.incomeStatementQuantity) || 0;
        summary.incomeStatementPrice += Number(item.incomeStatementPrice) || 0;

        // 本期销货保留原结构
        summary.exportQuantity += Number(item.exportQuantity) || 0;
        summary.exportPrice += Number(item.exportPrice) || 0;
    });

    return summary;
};

export default function SaleStockStatistics() {
    let wmService: any = systemConfig.wmService;
    const [form] = Form.useForm();
    const [queryCriteria, setQueryCriteria] = useState<any>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const notificationApi = useNotificationInstance();
    const [classificationWarehouseData, setClassificationWarehouseData] = useState<any>();
    const [rows, setRows] = useState<any>([]);
    const useContextData = useContext(SocketContext);
    const { contextData, setContextData } = useContextData;
    const contentRef = useRef<any>(null);

    const [productModel, setProductModel] = useState<any>([
        { id: 'rangeNumSearch', name: '品号范围搜索' },
        { id: 'similarNumSearch', name: '品号相似搜索' },
        { id: 'equalNumSearch', name: '品号相等搜索' },
        { id: 'similarNameSearch', name: '品名相似搜索' },
        { id: 'equalNameSearch', name: '品名相等搜索' },
    ]);

    const [selectProdutModel, setSelectProdutModel] = useState<any>('rangeNumSearch');

    const initialValues: any = {
        chooseTime: [dayjs().startOf('month'), dayjs()],
        isBuyerDetail: true,
        isCurrentAccountant: true,
        zeroStatus: 1,
        productModel: 'rangeNumSearch',
    };

    const [chooseTime, setChooseTime] = useState<any>([]);
    const [warehouseCategoriesData, setWarehouseCategoriesData] = useState<any>([]);
    const [columnsHead, setColumnsHead] = useState<Array<any>>();

    useEffect(() => {
        const fetchData = async () => {
            const warehouseCategoriesData = await fetchApi.GET(wmService, {
                apiId: 'warehouseCategories',
                apiExtend: 'showList',
            });

            if (warehouseCategoriesData.success) {
                setWarehouseCategoriesData(warehouseCategoriesData.data);
            }

            const classificationWarehouseData = await fetchApi.GET(wmService, {
                apiId: 'classificationWarehouse',
                apiExtend: 'showList',
            });

            if (classificationWarehouseData.success) {
                setClassificationWarehouseData(classificationWarehouseData.data);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFinish = () => {
        setIsLoading(true);
        form.validateFields()
            .then((values: any) => {
                fetchApi.GET(wmService, {
                    apiId: 'entrepotReport',
                    apiExtend: 'saleStockStatistics',
                    apiData: {
                        ...values,
                        entrepotId: contextData?.currentEntrepotId,
                        selfs: values.selfStart && values.selfEnd
                            ? {
                                selfStart: values.selfStart,
                                selfEnd: values.selfEnd,
                            }
                            : null,
                        mountDataOptions: {
                            userMount: {
                                isEmployees: true,
                                isDept: true,
                                isUserAreas: true,
                            },
                        },
                    },
                }).then(async (res: any) => {
                    let rows = res.data.rows;

                    let initColumnsHead: Array<any> = [
                        {
                            title: res.data.queryCriteria.entrepotName,
                            align: 'center',
                            children: [
                                {
                                    title: '品号',
                                    dataIndex: 'selfId',
                                    width: '5%',
                                    key: 'selfId',
                                    header: '品号',
                                    sortBy: 1,
                                    init: true,
                                    align: 'center',
                                },
                                {
                                    title: '品名',
                                    dataIndex: 'productName',
                                    width: '15%',
                                    key: 'productName',
                                    header: '品号',
                                    sortBy: 2,
                                    init: true,
                                    align: 'center',
                                },
                                {
                                    title: '单位',
                                    dataIndex: 'productUnitName',
                                    width: '6%',
                                    key: 'productUnitName',
                                    header: '单位',
                                    sortBy: 3,
                                    init: true,
                                    align: 'center',
                                },

                                /* 期初 */
                                {
                                    title: '期初',
                                    align: 'center',
                                    header: '期初',
                                    sortBy: 20,
                                    init: true,
                                    children: [
                                        {
                                            title: '数量',
                                            dataIndex: 'openingQuantity',
                                            key: 'openingQuantity',
                                            width: '7.4%',
                                            align: 'center',
                                            sorter: (a: any, b: any) =>
                                                Number(a.openingQuantity) - Number(b.openingQuantity),
                                        },
                                        {
                                            title: '金额',
                                            dataIndex: 'openingPrice',
                                            key: 'openingPrice',
                                            width: '7.4%',
                                            align: 'center',
                                            sorter: (a: any, b: any) =>
                                                Number(a.openingPrice) - Number(b.openingPrice),
                                        },
                                    ],
                                },

                                /* 本期进货 */
                                {
                                    title: '本期进货',
                                    align: 'center',
                                    header: '本期进货',
                                    sortBy: 20,
                                    init: true,
                                    children: [
                                        {
                                            title: '数量',
                                            align: 'center',
                                            dataIndex: 'importQuantity',
                                            key: 'importQuantity',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.importQuantity) - Number(b.importQuantity),
                                        },
                                        {
                                            title: '金额',
                                            align: 'center',
                                            dataIndex: 'importPrice',
                                            key: 'importPrice',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.importPrice) - Number(b.importPrice),
                                        },
                                    ],
                                },

                                /* 本期销货 */
                                {
                                    title: '本期销货',
                                    align: 'center',
                                    header: '本期销货',
                                    sortBy: 20,
                                    init: true,
                                    children: [
                                        {
                                            title: '数量',
                                            align: 'center',
                                            dataIndex: 'exportQuantity',
                                            key: 'exportQuantity',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.exportQuantity) - Number(b.exportQuantity),
                                        },
                                        {
                                            title: '金额',
                                            align: 'center',
                                            dataIndex: 'exportPrice',
                                            key: 'exportPrice',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.exportPrice) - Number(b.exportPrice),
                                        },
                                    ],
                                },

                                /* 本期调整 */
                                {
                                    title: '本期调整',
                                    align: 'center',
                                    header: '本期调整',
                                    sortBy: 20,
                                    init: true,
                                    children: [
                                        {
                                            title: '数量',
                                            align: 'center',
                                            dataIndex: 'incomeStatementQuantity',
                                            key: 'incomeStatementQuantity',
                                            width: '7.4%',
                                        },
                                        {
                                            title: '金额',
                                            align: 'center',
                                            dataIndex: 'incomeStatementPrice',
                                            key: 'incomeStatementPrice',
                                            width: '7.4%',
                                        },
                                    ],
                                },

                                /* 本期结存 */
                                {
                                    title: '本期结存',
                                    align: 'center',
                                    header: '本期结存',
                                    sortBy: 20,
                                    init: true,
                                    children: [
                                        {
                                            title: '数量',
                                            align: 'center',
                                            dataIndex: 'balanceQuantity',
                                            key: 'balanceQuantity',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.balanceQuantity) - Number(b.balanceQuantity),
                                        },
                                        {
                                            title: '金额',
                                            align: 'center',
                                            dataIndex: 'balancePrice',
                                            key: 'balancePrice',
                                            width: '7.4%',
                                            sorter: (a: any, b: any) =>
                                                Number(a.balancePrice) - Number(b.balancePrice),
                                        },
                                    ],
                                },
                            ],
                        },
                    ];

                    setColumnsHead(initColumnsHead);

                    if (rows && rows.length > 0) {
                        for (let i = 0; i < rows.length; i++) {
                            const productId = rows[i].productId;
                            const entrepotId = rows[i].entrepotId;
                            let openingPrice = Number(rows[i].openingPrice) || 0;
                            let openingQuantity = Number(rows[i].openingQuantity) || 0;

                            try {
                                const detail = await fetchApi.GET(wmService, {
                                    apiId: 'entrepotReport',
                                    apiExtend: 'checkPurAndTrData',
                                    apiData: {
                                        productId,
                                        entrepotId,
                                        chooseTime: values.chooseTime,
                                    },
                                });

                                if (detail.success) {
                                    rows[i].exportPrice = Number(detail.data.exportPrice) || 0;
                                    rows[i].exportQuantity = Number(detail.data.exportQuantity) || 0;
                                    rows[i].importQuantity = Number(detail.data.importQuantity) || 0;
                                    rows[i].importPrice = Number(detail.data.importPrice) || 0;

                                    rows[i].balanceQuantity =
                                        openingQuantity +
                                        rows[i].importQuantity -
                                        rows[i].exportQuantity;

                                    rows[i].balancePrice =
                                        openingPrice +
                                        rows[i].importPrice -
                                        rows[i].exportPrice;
                                }
                            } catch (err) {
                                console.log('err', err);
                            }
                        }

                        setIsLoading(false);
                    } else {
                        setIsLoading(false);
                    }

                    setRows(rows);
                    setQueryCriteria(res.data.queryCriteria);

                    showCustomNotification(notificationApi!, {
                        type: 'success',
                        message: '搜索成功',
                        description: 'success',
                    });
                }).catch((err: any) => {
                    console.log('err', err);
                    setIsLoading(false);
                    antMessage('error', '搜索失败', err.message + ' | status：' + err.errors?.status);
                });
            })
            .catch(() => {
                setIsLoading(false);
                showCustomNotification(notificationApi!, {
                    type: 'warning',
                    message: '无法通过数据校验',
                    description: '请输入所需数据!',
                });
            });
    };

    const handlePrint = () => {
        if (rows.length === 0) {
            antMessage('error', '打印失败', '无打印数据');
            return false;
        }

        const table = document.getElementsByTagName('table')[0];
        table.border = '1';
        table.style.width = '100%';
        table.style.fontSize = '12px';
        table.cellPadding = '0';
        table.cellSpacing = '0';

        const data = document.getElementById('aa')?.innerHTML;
        entrepotPrint.printEnReportEx(JSON.stringify(data), 'exDailyReport');
    };

    const changeProductModel = (e: any) => {
        setSelectProdutModel(e);
    };

    return (
        <>
            <Form
                form={form}
                onFinish={onFinish}
                initialValues={initialValues}
            >
                <Row>
                    <Col span={6}>
                        <Form.Item label="日期" name="chooseTime">
                            <RangePicker
                                value={chooseTime}
                                onCalendarChange={(dates) => {
                                    if (!dates || !dates[0]) {
                                        setChooseTime(dates || []);
                                        return;
                                    }
                                    if (dates[0].date() === 1) {
                                        setChooseTime(dates);
                                    }
                                }}
                                format="YYYY-MM-DD"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={3}>
                        <Form.Item label="大类" name="warehouseCategoriesIds">
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="大类"
                                optionFilterProp="children"
                                filterOption={() => true}
                            >
                                {getList.general(warehouseCategoriesData, 'warehouseCategoriesName')}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="品类" name="classificationWarehouses">
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="品类"
                                optionFilterProp="children"
                                onSearch={(e: any) =>
                                    getList.search('classificationWarehouse', setClassificationWarehouseData, e)
                                }
                                filterOption={() => true}
                            >
                                {getList.general(classificationWarehouseData, 'classificationWarehouseName')}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={3}>
                        <Form.Item label="产品模式" name="productModel">
                            <Select
                                onChange={changeProductModel}
                                showSearch
                                placeholder="产品模式"
                                optionFilterProp="children"
                                filterOption={() => true}
                            >
                                {getList.general(productModel, 'name')}
                            </Select>
                        </Form.Item>
                    </Col>

                    {selectProdutModel === 'rangeNumSearch' && (
                        <>
                            <Col span={3}>
                                <Form.Item label="品号" name="selfStart">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item colon={false} label="-" name="selfEnd">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    {(selectProdutModel === 'similarNameSearch' ||
                        selectProdutModel === 'equalNameSearch') && (
                            <Col span={3}>
                                <Form.Item label="品名" name="productName">
                                    <Input />
                                </Form.Item>
                            </Col>
                        )}

                    {(selectProdutModel === 'similarNumSearch' ||
                        selectProdutModel === 'equalNumSearch') && (
                            <Col span={3}>
                                <Form.Item label="品号" name="selfId">
                                    <Input />
                                </Form.Item>
                            </Col>
                        )}

                    <Col span={4}>
                        <Form.Item>
                            <Space>
                                <Button loading={isLoading} htmlType="submit">
                                    统计
                                </Button>
                                <Button onClick={handlePrint}>打印</Button>
                                <Button
                                    onClick={() =>
                                        DownloadExcel(rows, 'saleStockStatistics', {
                                            ...queryCriteria,
                                            columnsHead,
                                        })
                                    }
                                >
                                    下载EXCEL文件
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div id="aa" ref={contentRef}>
                <Table
                    title={() => (
                        <>
                            <div style={{ fontSize: 20, textAlign: 'center', marginBottom: 10 }}>
                                进销存统计表
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    textAlign: 'center',
                                    marginBottom: 10,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div>日期:{queryCriteria.date}</div>
                                <div>打印日期：{moment().format('YYYY-MM-DD')}</div>
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: 10,
                                }}
                            >
                                <div>品号:{queryCriteria.selfs}</div>
                            </div>
                        </>
                    )}
                    style={{ fontSize: '13px' }}
                    pagination={false}
                    rowKey={(record: any) => record.id}
                    columns={columnsHead}
                    dataSource={rows}
                    summary={(pageData) => {
                        const total = calculateSummary(pageData || []) as any;
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>

                                    <Table.Summary.Cell align='center' index={1}></Table.Summary.Cell>
                                    <Table.Summary.Cell  index={2}></Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={3}>{total.openingQuantity}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={4}>{total.openingPrice}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={5}>{total.importQuantity}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={6}>{total.importPrice}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={7}>{total.exportQuantity}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={8}>{total.exportPrice}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={9}></Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={10}></Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={11}>{total.balanceQuantity}</Table.Summary.Cell>
                                    <Table.Summary.Cell align='center' index={12}>{total.balancePrice}</Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </div>
        </>
    );
}

export let handleData = (rows: any, objKey: string) => {
    const newArr: Array<any> = [];
    if (typeof rows === 'undefined') return newArr;

    for (let i = 0; i < rows.length; i++) {
        newArr.push({
            [objKey]: rows[i],
        });
    }

    return newArr;
};